import { useMemo, useRef, useState } from "react";
import { deriveProject, parseInpComments, parseInpSections } from "../model/inp/parseInp.ts";
import { MAX_PROJECT_NAME_LENGTH, type ProjectSummary } from "../model/storage.ts";
import type { Theme } from "../model/theme.ts";
import { localeFor, type Language } from "../i18n/language.ts";
import { PLANE, type CrsChoice } from "../model/geo/crs.ts";
import { findEpsgByCode } from "../model/geo/epsgSearch.ts";
import { SAMPLE_PROJECTS, type SampleProject } from "../model/sampleProjects.ts";
import { useT } from "../i18n/hooks.ts";
import { SettingsMenu } from "./SettingsMenu.tsx";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { Logo } from "./Logo.tsx";
import { FolderOpenIcon } from "./FolderOpenIcon.tsx";
import { PlusIcon } from "./PlusIcon.tsx";
import { TrashIcon } from "./TrashIcon.tsx";
import { SpinnerIcon } from "./SpinnerIcon.tsx";
import { SampleThumbnail } from "./SampleThumbnail.tsx";
import { ProjectionPicker } from "./ProjectionPicker.tsx";

/**
 * Splits a translated sample name like "Victoria (Canada)" into its main
 * part and trailing "(Country)" so the example button can force the break
 * between them onto its own line — display only, the stored project name
 * is still the unbroken string.
 */
function splitSampleName(name: string): { main: string; country: string | null } {
  const match = /^(.*)\s(\([^()]*\))$/.exec(name);
  return match ? { main: match[1], country: match[2] } : { main: name, country: null };
}

export function Landing({
  savedProjects,
  onOpen,
  onOpenSample,
  onOpenSaved,
  onDeleteSaved,
  theme,
  onToggleTheme,
  language,
  onChangeLanguage,
  settingsMenuOpen,
  onToggleSettingsMenu,
  onCloseSettingsMenu,
}: {
  savedProjects: ProjectSummary[];
  onOpen: (
    rawSections: ReadonlyMap<string, string[][]>,
    comments: ReadonlyMap<string, string>,
    fileName: string,
    crs: string | null,
    name: string
  ) => void;
  /** Same shape as `onOpen`, for a bundled sample — App.tsx marks the resulting project `ephemeral` so it autosaves during the session but doesn't linger in "Your projects" afterward. */
  onOpenSample: (
    rawSections: ReadonlyMap<string, string[][]>,
    comments: ReadonlyMap<string, string>,
    fileName: string,
    crs: string | null,
    name: string
  ) => void;
  onOpenSaved: (id: string) => void;
  onDeleteSaved: (id: string) => void;
  theme: Theme;
  onToggleTheme: () => void;
  language: Language;
  onChangeLanguage: (language: Language) => void;
  settingsMenuOpen: boolean;
  onToggleSettingsMenu: () => void;
  onCloseSettingsMenu: () => void;
}) {
  const t = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  /** The `key` of whichever example is currently being fetched, if any — only that one button shows a spinner. */
  const [loadingSample, setLoadingSample] = useState<string | null>(null);
  // Set once a file is picked and parsed, holding it here rather than
  // opening immediately so the user can name the project and answer "is
  // this georeferenced?" (ProjectionPicker.tsx) first. Cleared by either
  // confirming (hands off to onOpen) or cancelling.
  const [pending, setPending] = useState<{
    rawSections: ReadonlyMap<string, string[][]>;
    comments: ReadonlyMap<string, string>;
    fileName: string;
  } | null>(null);
  const [name, setName] = useState("");
  const [crs, setCrs] = useState<CrsChoice>(PLANE);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Only needed to sanity-check a CRS pick against the project's actual
  // coordinates (ProjectionPicker.tsx) — re-derived only when a different
  // file is picked, not on every keystroke.
  const pendingProject = useMemo(
    () => (pending ? deriveProject(pending.rawSections) : null),
    [pending]
  );

  function openPending(text: string, fileName: string) {
    const rawSections = parseInpSections(text);
    const comments = parseInpComments(text);
    const title = (rawSections.get("TITLE") ?? [])
      .map((row) => row[0])
      .join(" ")
      .trim();
    // Clamped here too, not just via the input's `maxLength` — that only
    // limits further keystrokes, never a value this long set programmatically.
    setName((title || fileName.replace(/\.inp$/i, "")).slice(0, MAX_PROJECT_NAME_LENGTH));
    setCrs(PLANE);
    setPending({ rawSections, comments, fileName });
  }

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;
    setError(null);
    try {
      openPending(await file.text(), file.name);
    } catch (err) {
      // A genuinely unpredictable browser/parser message (unlike
      // handleLoadSample's own catch below, which always sets a fully
      // translated string) — wrapped in a translated sentence structure
      // rather than shown raw, same pattern as storage.errorMessage/
      // gisImport.readFailed/projectionPicker.searchFailed.
      setError(
        t("landing.openFileError", { message: err instanceof Error ? err.message : String(err) })
      );
    }
  }

  /**
   * Opens a bundled sample directly — no name field, no ProjectionPicker.tsx,
   * since both are already known for it. A sample with no confirmed CRS
   * (`epsgCode: null`) opens in plane mode instead.
   */
  async function handleLoadSample(sample: SampleProject) {
    setError(null);
    setLoadingSample(sample.key);
    try {
      const [response, epsgEntry] = await Promise.all([
        fetch(sample.url),
        sample.epsgCode ? findEpsgByCode(sample.epsgCode) : Promise.resolve(null),
      ]);
      if (!response.ok || (sample.epsgCode && !epsgEntry)) {
        throw new Error(t("landing.trySampleError"));
      }
      const text = await response.text();
      const rawSections = parseInpSections(text);
      const comments = parseInpComments(text);
      onOpenSample(
        rawSections,
        comments,
        sample.fileName,
        epsgEntry?.proj4 ?? null,
        t(sample.nameKey)
      );
    } catch {
      setError(t("landing.trySampleError"));
    } finally {
      setLoadingSample(null);
    }
  }

  function handleCreateEmpty() {
    setError(null);
    setName(t("landing.untitledProjectName"));
    setCrs(PLANE);
    setPending({ rawSections: new Map(), comments: new Map(), fileName: "" });
  }

  if (pending && pendingProject) {
    const trimmedName = name.trim();
    return (
      <div className="landing">
        <div className="floating-controls">
          <SettingsMenu
            theme={theme}
            onToggleTheme={onToggleTheme}
            language={language}
            onChangeLanguage={onChangeLanguage}
            open={settingsMenuOpen}
            onToggle={onToggleSettingsMenu}
            onClose={onCloseSettingsMenu}
            floating
          />
        </div>
        <div className="landing-card">
          <h1>{t("landing.newProjectTitle")}</h1>
          {pending.fileName && <p className="landing-tagline">{pending.fileName}</p>}
          <label className="landing-name-field">
            {t("landing.projectNameLabel")}
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={MAX_PROJECT_NAME_LENGTH}
              autoFocus
            />
          </label>
          <ProjectionPicker project={pendingProject} value={crs} onChange={setCrs} />
          <div className="landing-pending-actions">
            <button className="button" onClick={() => setPending(null)}>
              {t("landing.back")}
            </button>
            <button
              className="button button-primary"
              disabled={!trimmedName}
              onClick={() =>
                onOpen(
                  pending.rawSections,
                  pending.comments,
                  pending.fileName,
                  crs.proj4,
                  trimmedName
                )
              }
            >
              {t("landing.open")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing">
      <div className="floating-controls">
        <SettingsMenu
          theme={theme}
          onToggleTheme={onToggleTheme}
          language={language}
          onChangeLanguage={onChangeLanguage}
          open={settingsMenuOpen}
          onToggle={onToggleSettingsMenu}
          onClose={onCloseSettingsMenu}
          floating
        />
      </div>
      <div className="landing-card">
        <div className="landing-logo">
          <Logo size={64} />
          <h1>{t("landing.title")}</h1>
        </div>
        <p className="landing-tagline">{t("landing.tagline")}</p>
        <div className="landing-open-actions">
          <button className="button button-primary" onClick={() => inputRef.current?.click()}>
            <FolderOpenIcon />
            {t("landing.openButton")}
          </button>
          <button className="button" onClick={handleCreateEmpty}>
            <PlusIcon />
            {t("landing.newEmptyButton")}
          </button>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".inp"
          className="landing-file-input"
          onChange={(e) => void handleFiles(e.target.files)}
        />
        {error && <p className="error">{error}</p>}

        {savedProjects.length > 0 && (
          <div className="landing-saved">
            <p className="landing-saved-title">{t("landing.yourProjects")}</p>
            <ul>
              {savedProjects.map((p) => (
                <li key={p.id} className="landing-saved-row">
                  <button className="landing-saved-button" onClick={() => onOpenSaved(p.id)}>
                    <span className="landing-saved-name">{p.name}</span>
                    <span className="landing-saved-date">
                      {new Date(p.lastModified).toLocaleString(localeFor(language), {
                        year: "numeric",
                        month: "short", // Not "numeric" — "1/6" reads as ambiguous (day or month first?).
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </button>
                  <button
                    className="button-icon button-icon-danger landing-saved-delete"
                    aria-label={t("landing.deleteProject", { name: p.name })}
                    title={t("landing.deleteProject", { name: p.name })}
                    onClick={() => setConfirmDeleteId(p.id)}
                  >
                    <TrashIcon />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="landing-examples">
          <p className="landing-examples-title">{t("landing.orTryAnExample")}</p>
          <ul>
            {SAMPLE_PROJECTS.map((sample) => {
              const { main, country } = splitSampleName(t(sample.nameKey));
              return (
                <li key={sample.key}>
                  <button
                    className="landing-example"
                    onClick={() => void handleLoadSample(sample)}
                    disabled={loadingSample !== null}
                  >
                    {loadingSample === sample.key ? (
                      <SpinnerIcon />
                    ) : (
                      <SampleThumbnail sampleKey={sample.key} />
                    )}
                    <span>
                      {main}
                      {country && (
                        <>
                          <br />
                          {country}
                        </>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      {confirmDeleteId &&
        (() => {
          const target = savedProjects.find((p) => p.id === confirmDeleteId);
          if (!target) return null;
          return (
            <ConfirmDialog
              title={t("projectMenu.deleteTitle")}
              message={t("projectMenu.deleteMessage", { name: target.name })}
              confirmLabel={t("projectMenu.deleteConfirm")}
              onConfirm={() => onDeleteSaved(target.id)}
              onClose={() => setConfirmDeleteId(null)}
            />
          );
        })()}
    </div>
  );
}
